// web/src/scene/rtls/PlaybackAdvancer.tsx
//
// Auto-play advancer. Runs inside the R3F render loop — naturally pauses
// when the tab is hidden. Dispatches setPlaybackTimestamp every frame so
// the rendered asset position follows the path continuously (no 10 Hz
// sawtooth). Stops at maxTs and flips back to live mode.

import React, { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { useAppDispatch } from '../../app/useAppDispatch'
import { useAppSelector } from '../../app/useAppSelector'
import { rtlsActions } from '@shared/slices/rtlsSlice'

// Cap per-frame delta so a tab-hidden pause or stalled first frame doesn't
// launch the scrubber to maxTs on a single long delta.
const MAX_FRAME_DELTA_SEC = 0.1

interface PlaybackAdvancerProps {
  maxTs: number
}

export const PlaybackAdvancer: React.FC<PlaybackAdvancerProps> = ({ maxTs }) => {
  const dispatch = useAppDispatch()
  const playbackPlaying = useAppSelector(s => s.rtls.playbackPlaying)
  const playbackSpeed = useAppSelector(s => s.rtls.playbackSpeed)
  const playbackTs = useAppSelector(s => s.rtls.playbackTimestamp)

  // Keep the latest playbackTs in a ref so useFrame always reads the freshest
  // value (Redux re-renders this component on each dispatch; the closure
  // already captures the current value but reading from ref is clearer).
  const playbackTsRef = useRef(playbackTs)
  playbackTsRef.current = playbackTs

  useFrame((_, delta) => {
    if (!playbackPlaying) return
    const current = playbackTsRef.current
    if (current == null) return

    const advance = Math.min(delta, MAX_FRAME_DELTA_SEC) * 1000 * playbackSpeed
    const next = current + advance

    if (next >= maxTs) {
      dispatch(rtlsActions.setPlaybackTimestamp(maxTs))
      dispatch(rtlsActions.setPlaybackPlaying(false))
      // Match mobile — returning to live mode restores all assets (via the
      // isolation guard in RTLSOverlay/TrailRenderer).
      dispatch(rtlsActions.setPlaybackMode('live'))
    } else {
      dispatch(rtlsActions.setPlaybackTimestamp(next))
    }
  })

  return null
}
